'use client';

import 'BomdisoftComponents/brand/styles.css';
import { toBrandVariables } from 'BomdisoftComponents/brand/render';
import Image from 'next/image';
import Link from 'next/link';
import type { CSSProperties } from 'react';

export type BrandProps = {
  href?: string;
  logoSrc?: string;
  size?: number;
  testId?: string;
  wordmarkSuffix: string;
  /** Space between the mark and the wordmark (theme units). Default 0.75; set small when the mark
   * IS the word's first letter so the whole thing reads as one word (e.g. W + orkadabra). */
  gap?: number;
  /** Explicit rendered mark width in px. Defaults to `size`. Pass the logo's real dimensions so a
   * non-square mark renders at the right size/aspect (e.g. a wide mark at cap height). */
  logoWidth?: number;
  /** Explicit rendered mark height in px. Defaults to `size`. */
  logoHeight?: number;
  /** Wordmark inline-start margin in px, so the nudge follows the writing direction. Negative
   * overlaps the wordmark onto the mark when the mark IS the word's first letter and the glyphs
   * must tuck together tighter than `gap` allows (e.g. an E-shaped mark + "stimojo"). Default 0,
   * no offset. */
  wordmarkMarginInlineStart?: number;
  /** Wordmark vertical margin in px, applied as margin-bottom to nudge the baseline against a
   * mark taller than the cap height. Default 0 — no offset. */
  wordmarkMarginBottom?: number;
  /** Skip lazy-loading and preload the mark. Set it in a header, where the brand is above the
   * fold and lazy-loading it shows an empty box first. Leave it off in a footer. */
  priority?: boolean;
};

export const Brand = ({ href, logoSrc = '/images/logo.png', size = 24, testId = 'brand', wordmarkSuffix, gap = 0.75, logoWidth, logoHeight, wordmarkMarginInlineStart = 0, wordmarkMarginBottom = 0, priority = false }: BrandProps) => {
  const vars = toBrandVariables({ gap, logoHeight, logoWidth, size, wordmarkMarginBottom, wordmarkMarginInlineStart }) as CSSProperties;

  // next/image rather than a bare <img>: the source mark is a 192px PNG and every
  // brand renders it at 20-28px, so a raw tag ships roughly 21KB to paint 28px and
  // misses both the WebP encode and the immutable /_next/image cache. alt is empty
  // because the wordmark beside it already carries the name.
  const inner = (
    <>
      <Image alt='' className='bomdisoft-brand-logo' height={logoHeight ?? size} priority={priority} src={logoSrc} width={logoWidth ?? size} />
      <span className='bomdisoft-brand-wordmark'>{wordmarkSuffix}</span>
    </>
  );

  if (href) {
    return (
      <Link className='bomdisoft-brand' data-testid={testId} href={href} style={vars}>
        {inner}
      </Link>
    );
  }

  return (
    <span className='bomdisoft-brand' data-testid={testId} style={vars}>
      {inner}
    </span>
  );
};
